// src/v3/ui/_common/components/ui/progress.tsx

import { X } from "lucide-react";
import { cn } from "../../lib/utils";

const PROGRESS_VARIANT_OBJ = {
  DEFAULT: "default",
  SUCCESS: "success",
  ERROR: "error",
} as const;

interface ProgressProps {
  value: number | undefined;
  label?: string;
  variant?: (typeof PROGRESS_VARIANT_OBJ)[keyof typeof PROGRESS_VARIANT_OBJ];
  onCancel?: () => void;
}

export function Progress({ value, label, variant = "default", onCancel }: ProgressProps) {
  const variantClass =
    variant === PROGRESS_VARIANT_OBJ.SUCCESS
      ? "bg-green-500"
      : variant === PROGRESS_VARIANT_OBJ.ERROR
        ? "bg-red-500"
        : "bg-primary";
  const isIndeterminate = value === undefined;
  const clamped = isIndeterminate ? 0 : Math.max(0, Math.min(100, value));

  return (
    <div className="space-y-1">
      {label && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">{label}</span>
          {!isIndeterminate && (
            <span className="font-medium">{clamped.toFixed(0)}%</span>
          )}
        </div>
      )}
      <div className="flex items-center gap-2">
        <div className="h-2 flex-1 overflow-hidden rounded-full bg-muted">
          {isIndeterminate ? (
            <div
              className={cn("h-full w-1/3 animate-pulse rounded-full", variantClass)}
            />
          ) : (
            <div
              className={cn("h-full rounded-full transition-all", variantClass)}
              style={{ width: `${clamped}%` }}
            />
          )}
        </div>
        {onCancel && (
          <button
            onClick={onCancel}
            className="rounded-md border p-1 hover:bg-accent"
            aria-label="Cancel"
          >
            <X className="h-3 w-3" />
          </button>
        )}
      </div>
    </div>
  );
}
